import {
  calculateRefrigeratorSize,
  REFRIGERATOR_SIZE_DEFAULTS,
  type CookingFrequency,
  type RefrigeratorSizeResult,
  type ShoppingFrequency,
} from '@/lib/tools/refrigerator-size'
import {
  ENERGY_STAR_PLANNING_LIFETIME_YEARS,
  REFRIGERATOR_LIFESPAN_QUESTION,
  refrigeratorLifespanAnswer,
} from '@/lib/tools/refrigerator-lifespan'

/**
 * Two answers the site prints in many places, stated once (FRIDGE-1435).
 *
 * Measured on the served documents 10/08/2026: "what size refrigerator does a
 * family of four need" had three answers (homepage 22–28 cu. ft., /llms.txt
 * 18–22, the size calculator 23) and "how long does a refrigerator last" had
 * three more (homepage 8–10 years, /llms.txt 10–15 and 10–14). AI assistants
 * read /llms.txt first and the homepage second; two numbers for one question,
 * both attributed to Fridge.com, is the contradiction that costs a citation.
 *
 * Each answer is computed from the site's own source, never typed:
 *   - size: the refrigerator size calculator, run with the cooking and
 *     shopping habits it opens with (lib/tools/refrigerator-size.ts);
 *   - lifespan: the ENERGY STAR planning sentence the lifespan calculator
 *     publishes (lib/tools/refrigerator-lifespan.ts, FRIDGE-902).
 *
 * Rendered by the homepage quick answers and quiz, the /llms.txt template
 * (app/llms-txt/route.ts) and the FAQ copy that repeats either question.
 * tests/refrigerator-answer-facts-fridge-1435.test.ts reads the served homepage
 * and /llms.txt and fails on any other household size or refrigerator lifespan.
 */

export interface AnswerFact {
  question: string
  answer: string
  /** Where the value comes from. */
  source: string
  /** The Fridge.com page a reader can check it on. */
  sourcePath: string
  /** When the value was last reconciled against its source (YYYY-MM-DD). */
  asOf: string
}

const COOKING_PHRASE: Record<CookingFrequency, string> = {
  daily: 'cooks daily',
  weekly: 'cooks a few times a week',
  rarely: 'rarely cooks',
}

const SHOPPING_PHRASE: Record<ShoppingFrequency, string> = {
  daily: 'shops as needed',
  weekly: 'shops weekly',
  biweekly: 'shops every two weeks',
  monthly: 'shops in monthly bulk',
}

const NUMBER_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** The calculator's result for a household of `people` with the habits it opens with. */
export function householdSize(people: number): RefrigeratorSizeResult {
  const result = calculateRefrigeratorSize({ ...REFRIGERATOR_SIZE_DEFAULTS, householdSize: String(people) })
  if (!result) throw new Error(`the refrigerator size calculator rejected a household of ${people}`)
  return result
}

/** The calculator's recommended capacity for `people`, in cubic feet. */
export function recommendedCuFtFor(people: number): number {
  return householdSize(people).recommendedSize
}

/**
 * One sentence: what the size calculator recommends for a family of `people`.
 * Short on purpose: it is the homepage card answer too, and the canvas allows
 * no answer over four lines (owner 09/16/2026; this fits four lines on the
 * narrowest four-column card, 224 px at a 1149 px window).
 */
export function familySizeAnswer(people: number): string {
  const r = householdSize(people)
  const habits = `${COOKING_PHRASE[REFRIGERATOR_SIZE_DEFAULTS.cookingFrequency]} and ${SHOPPING_PHRASE[REFRIGERATOR_SIZE_DEFAULTS.shoppingFrequency]}`
  return `About ${r.recommendedSize} cu. ft. (${r.minSize}–${r.maxSize}) for a family of ${NUMBER_WORD[people] ?? people} that ${habits}, per the Fridge.com size calculator.`
}

const four = householdSize(4)

export const FAMILY_OF_FOUR_SIZE: AnswerFact & { recommendedCuFt: number; minCuFt: number; maxCuFt: number } = {
  question: 'What size refrigerator does a family of 4 need?',
  answer: familySizeAnswer(4),
  source: 'Fridge.com refrigerator size calculator (4 cu. ft. per person plus cooking and shopping allowances)',
  sourcePath: '/tools/refrigerator-size-calculator',
  asOf: '2026-10-08',
  recommendedCuFt: four.recommendedSize,
  minCuFt: four.minSize,
  maxCuFt: four.maxSize,
}

export const REFRIGERATOR_LIFESPAN: AnswerFact & { planningYears: number; shortAnswer: string } = {
  question: REFRIGERATOR_LIFESPAN_QUESTION,
  answer: refrigeratorLifespanAnswer(),
  // The same fact in four lines or fewer, for the homepage card.
  shortAnswer: `ENERGY STAR's savings comparison assumes a ${ENERGY_STAR_PLANNING_LIFETIME_YEARS}-year refrigerator lifetime, not a prediction for yours.`,
  source: 'ENERGY STAR savings comparison, as cited by the Fridge.com refrigerator lifespan calculator',
  sourcePath: '/tools/refrigerator-lifespan-calculator',
  asOf: '2026-10-08',
  planningYears: ENERGY_STAR_PLANNING_LIFETIME_YEARS,
}

/**
 * Renders the answers into /llms.txt. The template marks each answer cell as
 * `<!-- FAMILY_SIZE_ANSWER_<people> -->…<!-- /FAMILY_SIZE_ANSWER_<people> -->`
 * or `<!-- REFRIGERATOR_LIFESPAN_ANSWER -->…<!-- /REFRIGERATOR_LIFESPAN_ANSWER -->`;
 * whatever sits between the markers is replaced with the computed sentence, so
 * the served document states these facts the way every other surface does.
 * The text stored in the template is the fallback, and the FRIDGE-1435 guard
 * keeps it equal to this rendering.
 */
export function renderLlmsAnswerFacts(text: string): string {
  return text
    .replace(
      /(<!-- FAMILY_SIZE_ANSWER_(\d+) -->)[\s\S]*?(<!-- \/FAMILY_SIZE_ANSWER_\2 -->)/g,
      (_m, open: string, people: string, close: string) => `${open}${familySizeAnswer(Number(people))}${close}`,
    )
    .replace(
      /(<!-- REFRIGERATOR_LIFESPAN_ANSWER -->)[\s\S]*?(<!-- \/REFRIGERATOR_LIFESPAN_ANSWER -->)/g,
      (_m, open: string, close: string) => `${open}${REFRIGERATOR_LIFESPAN.answer}${close}`,
    )
}
